import {Box, Link, Typography} from '@mui/material';
import {DownloadPdfButton} from './DownloadPdfButton.tsx';
import {RefObject, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';

const HEADER_OFFSET = 72;
const TOC_WIDTH = 240;
const ACTIVE_ZONE_BOTTOM_MARGIN = '70%';

interface TocHeading {
  id: string;
  text: string;
  level: 2 | 3;
}

interface OnThisPageProps {
  containerRef: RefObject<HTMLElement | null>;
  slug: string;
}

export const OnThisPage: React.FC<OnThisPageProps> = ({containerRef, slug}) => {
  const {t, i18n} = useTranslation();
  const [headings, setHeadings] = useState<TocHeading[]>([]);
  const [activeId, setActiveId] = useState<string | null>(null);

  // Language is a dependency because switching it keeps the same slug but swaps the
  // rendered article (and its headings) in place.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) {
      setHeadings([]);
      return;
    }
    const headingNodes = Array.from(container.querySelectorAll<HTMLElement>('h2[id], h3[id]'));
    setHeadings(
      headingNodes.map(node => ({
        id: node.id,
        text: node.textContent ?? '',
        level: node.tagName === 'H2' ? 2 : 3,
      })),
    );
  }, [containerRef, slug, i18n.language]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || headings.length === 0) {
      setActiveId(null);
      return;
    }

    const visibleIds = new Set<string>();
    const observer = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            visibleIds.add(entry.target.id);
          } else {
            visibleIds.delete(entry.target.id);
          }
        }
        // Highlight the topmost in-view heading so the active section matches what the reader is reading down to.
        const firstVisible = headings.find(h => visibleIds.has(h.id));
        if (firstVisible) {
          setActiveId(firstVisible.id);
        }
      },
      {rootMargin: `-${HEADER_OFFSET}px 0px -${ACTIVE_ZONE_BOTTOM_MARGIN} 0px`, threshold: 0},
    );

    for (const heading of headings) {
      const el = container.querySelector(`#${CSS.escape(heading.id)}`);
      if (el) {
        observer.observe(el);
      }
    }

    return () => observer.disconnect();
  }, [containerRef, headings]);

  const handleClick = (event: React.MouseEvent<HTMLAnchorElement>, id: string): void => {
    event.preventDefault();
    const el = containerRef.current?.querySelector(`#${CSS.escape(id)}`);
    if (el) {
      el.scrollIntoView({behavior: 'smooth', block: 'start'});
      setActiveId(id);
      window.history.replaceState(null, '', `#${id}`);
    }
  };

  return (
    <Box
      component="nav"
      aria-label={t('on-this-page')}
      sx={{
        display: {xs: 'none', md: 'block'},
        position: 'sticky',
        top: `calc(${HEADER_OFFSET}px + 1rem)`,
        alignSelf: 'flex-start',
        flexShrink: 0,
        width: TOC_WIDTH,
        maxHeight: 'calc(100vh - 100px)',
        overflowY: 'auto',
        pl: 2,
        pr: 1,
        py: 1,
        borderLeft: 1,
        borderColor: 'divider',
      }}
    >
      <DownloadPdfButton />
      {headings.length > 0 && (
        <>
          <Typography
            variant="overline"
            sx={{display: 'block', fontWeight: 600, color: 'text.secondary', mt: 2, mb: 1}}
          >
            {t('on-this-page')}
          </Typography>
          <Box component="ul" sx={{listStyle: 'none', m: 0, p: 0}}>
            {headings.map(heading => {
              const isActive = heading.id === activeId;
              return (
                <Box component="li" key={heading.id} sx={{pl: heading.level === 3 ? 1.5 : 0, my: 0.5}}>
                  <Link
                    href={`#${heading.id}`}
                    onClick={e => handleClick(e, heading.id)}
                    underline="none"
                    variant="body2"
                    sx={{
                      display: 'block',
                      color: isActive ? 'primary.main' : 'text.secondary',
                      fontWeight: isActive ? 600 : 400,
                      '&:hover': {color: 'primary.main'},
                    }}
                  >
                    {heading.text}
                  </Link>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Box>
  );
};
